import React, {ChangeEvent, FormEvent, useEffect, useRef, useState} from "react";
import {ProductListFilterItemRange} from "@connox/typescript-library";
import {useProductListContext} from "../../ProductListProvider/ProductListProvider";
import {ProductListFilterItemRangeValue} from "@connox/typescript-library";
import Slider from "rc-slider";
import {useTranslation} from "react-i18next";

// rc-slider handles classnames internally, so css modules won't work here
import "./FormRange.scss";
import styles from "./ProductFilterRange.module.scss";
import productListFilterstyles from "../ProductListFilter.module.scss";
import containerStyles from "../AppToggleContainer/AppToggleContainer.module.scss";
import {Button} from "../../../lib/form/Button/Button";
import {useMediaQuery} from "react-responsive";
import selectStyles from "../../../lib/form/Select/Select.module.scss";
import nlOptionsFormStyles from "../../../newsletter/NewsLetterOptionsForm.module.scss";

interface ProductFilterRangeProps {
  item: ProductListFilterItemRange;
  onSelect?: (value: ProductListFilterItemRangeValue) => void;
}

const Range = Slider.createSliderWithTooltip(Slider.Range);

export const genericFormatter = new Intl.NumberFormat([], {minimumFractionDigits: 1, maximumFractionDigits: 1});
export const currencyFormatter = new Intl.NumberFormat([], {
  style: "currency",
  currency: window.c_ActiveCurrency,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const ProductFilterRange: React.FC<ProductFilterRangeProps> = ({
  item,
  onSelect = () => {
    return;
  },
}) => {
  const {t} = useTranslation();
  const {state, dispatch} = useProductListContext();
  const [value, setValue] = useState<ProductListFilterItemRangeValue>(item.value);
  const isMobile = useMediaQuery({query: "(max-width: 480px)"});
  const [limit, setLimit] = useState<{min: number; max: number}>({min: 0, max: 0});
  const [inputValues, setInputValues] = useState<{min: string; max: string}>({
    min: formatValue(item.value.min),
    max: formatValue(item.value.max),
  });

  const [focused, setFocused] = useState<"min" | "max">();
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (evt: MouseEvent) => {
      if (
        !(containerRef.current === evt.target || containerRef.current?.contains(evt.target as HTMLElement)) &&
        !isMobile
      ) {
        setOpen(false);
      }
    };
    document.body.addEventListener("click", handleClick);

    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    const initialValue = state.filters[item.field] as ProductListFilterItemRangeValue;
    if (initialValue) {
      setValue(initialValue);
      if (!focused) {
        setInputValues({
          min: formatValue(initialValue.min),
          max: formatValue(initialValue.max),
        });
      }
    } else {
      setValue(limit);
      if (!focused) {
        setInputValues({
          min: formatValue(limit.min),
          max: formatValue(limit.max),
        });
      }
    }
  }, [state.filters[item.field]]);

  useEffect(() => {
    const filterValue = state.filters[item.field] as ProductListFilterItemRangeValue;
    if (!filterValue) {
      setValue(limit);
      if (!focused) {
        setInputValues({
          min: formatValue(limit.min),
          max: formatValue(limit.max),
        });
      }
    } else {
      if (limit.max >= filterValue.max && limit.min <= filterValue.min) {
        setValue(filterValue);
        if (!focused) {
          setInputValues({
            min: formatValue(filterValue.min),
            max: formatValue(filterValue.max),
          });
        }
      }
      if (limit.min > filterValue.min) {
        setValue({...value, min: limit.min});
        setInputValues({...inputValues, min: formatValue(limit.min)});
      }
      if (limit.max < filterValue.max) {
        setValue({...value, max: limit.max});
        setInputValues({...inputValues, max: formatValue(limit.max)});
      }
      if (limit.min > filterValue.min && limit.max < filterValue.max) {
        dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
      }
    }
  }, [limit.min, limit.max]);

  useEffect(() => {
    const minMaxValue = state.rangeFilterMap?.get(item.field);
    if (minMaxValue) {
      setLimit({
        min: minMaxValue.min,
        max: minMaxValue.max,
      });
    }
  }, [state.rangeFilterMap?.get(item.field)]);

  if (limit.min === limit.max) {
    return null;
  }

  const onRangeChangeComplete = () => {
    submitRange(value);
  };

  const submitRange = (_value: ProductListFilterItemRangeValue) => {
    if (isMobile && onSelect) {
      onSelect(value);

      return;
    }
    if (_value.min === limit.min && _value.max === limit.max) {
      dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
    } else {
      dispatch({type: "SET_FILTER", payload: {field: item.field, value: _value}});
    }
  };

  const onRangeChange = (val: [number, number]) => {
    setValue({min: val[0], max: val[1]});
    setInputValues({min: formatValue(val[0]), max: formatValue(val[1])});
  };

  const title = () => {
    if (item.field === "price") {
      const currency =
        currencyFormatter.formatToParts(42).find((part) => part.type === "currency")?.value ?? window.c_ActiveCurrency;

      return (
        <>
          {item.count ? (
            <>
              {t("Preis in {{currency}}", {currency})}{" "}
              <span className={productListFilterstyles.count}>{item.count}</span>
            </>
          ) : (
            t("Preis (in {{currency}})", {currency})
          )}
        </>
      );
    }

    return (
      <>
        {item.count ? (
          <>
            {item.name || t<string>(item.field)} <span className={productListFilterstyles.count}>{item.count}</span>
          </>
        ) : (
          item.name || t(item.field)
        )}
      </>
    );
  };

  const tipFormatter = () => {
    if (item.field === "price") {
      return (tipValue) => currencyFormatter.format(tipValue);
    } else {
      return (tipValue) => genericFormatter.format(tipValue) + "\u00A0g";
    }
  };

  function formatValue(_value: number): string {
    if (item.field === "price") {
      return currencyFormatter.format(_value);
    } else {
      return genericFormatter.format(_value) + "\u00A0g";
    }
  }

  const onBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as "min" | "max";
    setFocused(undefined);
    const inputValue = Number(event.target.value);

    // validation
    if (name === "min") {
      if (inputValue < limit.min) {
        setInputValues({min: formatValue(limit.min), max: formatValue(value.max)});
        setValue({min: limit.min, max: value.max});
        onSelect({min: limit.min, max: value.max});

        return;
      }
      if (inputValue >= limit.max || (inputValue >= value.max && value.max === limit.max)) {
        setInputValues({min: formatValue(value.min), max: formatValue(value.max)});
        onSelect(value);

        return;
      }

      if (inputValue >= value.max && value.max < limit.max) {
        setInputValues({min: formatValue(inputValue), max: formatValue(limit.max)});
        setValue({min: inputValue, max: limit.max});
        onSelect({min: inputValue, max: limit.max});

        return;
      }
    }

    if (name === "max") {
      if (inputValue > limit.max) {
        setInputValues({min: formatValue(value.min), max: formatValue(limit.max)});
        setValue({min: value.min, max: limit.max});
        onSelect({min: value.min, max: limit.max});

        return;
      }

      if (inputValue <= limit.min || (inputValue <= value.min && value.min === limit.min)) {
        setInputValues({min: formatValue(value.min), max: formatValue(value.max)});
        onSelect(value);

        return;
      }

      if (inputValue <= value.min && value.min > limit.min) {
        setInputValues({min: formatValue(limit.min), max: formatValue(inputValue)});
        setValue({min: limit.min, max: inputValue});
        onSelect({min: limit.min, max: inputValue});

        return;
      }
    }

    // end validation
    setInputValues({
      min: formatValue(Number(inputValues.min)),
      max: formatValue(Number(inputValues.max)),
      [name]: formatValue(Number(inputValue)),
    });
    setValue({...value, [name]: inputValue});
    onSelect({...value, [name]: inputValue});
  };

  const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as "min" | "max";
    setFocused(name);
    setInputValues({min: inputValues.min.replace(/\D/g, ""), max: inputValues.max.replace(/\D/g, "")});
  };

  const submitData = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let max = Number(inputValues.max.replace(/\D/g, ""));
    let min = Number(inputValues.min.replace(/\D/g, ""));

    // validation
    if (min < limit.min) {
      setInputValues({...inputValues, min: String(limit.min)});
      min = limit.min;
    }

    if (min > limit.max || max < limit.min) {
      setInputValues({min: String(value.min), max: String(value.min)});
      min = value.min;
      max = value.max;
    } else if (min >= max) {
      if (focused === "min") {
        if (max === limit.max) {
          setInputValues({...inputValues, min: String(value.min)});
          min = value.min;
        } else {
          setInputValues({...inputValues, max: String(limit.max)});
          max = limit.max;
        }
      } else if (focused === "max") {
        if (min === limit.min) {
          setInputValues({...inputValues, max: String(value.max)});
          max = value.max;
        } else {
          setInputValues({...inputValues, min: String(limit.min)});
          min = limit.min;
        }
      }
    }

    if (max > limit.max) {
      setInputValues({...inputValues, max: String(limit.max)});
      max = limit.max;
    }

    // end validation

    setOpen(false);
    const valueToSubmit = {min, max};
    setValue(valueToSubmit);
    submitRange(valueToSubmit);
  };

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as "min" | "max";

    // (/\D/g) is RegExp detect anything but not numbers
    setInputValues({...inputValues, [name]: event.target.value.replace(/\D/g, "")});
  };

  return (
    <div className={containerStyles.item} ref={containerRef}>
      <div
        onClick={() => {
          setOpen(!open);
        }}
        className={[
          selectStyles.wrapper,
          nlOptionsFormStyles.select,
          containerStyles.formElementContainer,
          containerStyles.customArrow,
          open ? containerStyles.isOpen : "",
          open ? styles.isOpen : "",
        ].join(" ")}
      >
        {title()}
      </div>
      {open && (
        <div
          className={`range-container ${styles.rangeWrapper}`}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Range
            max={limit.max}
            min={limit.min}
            onChange={onRangeChange}
            tipFormatter={tipFormatter()}
            pushable={1}
            onAfterChange={onRangeChangeComplete}
            value={[value.min, value.max]}
            allowCross={false}
          />
          <form onSubmit={submitData}>
            <div className={styles.inputsWrapper}>
              <input
                type={"text"}
                name="min"
                value={inputValues.min}
                onBlur={onBlurInput}
                onFocus={onFocusInput}
                onChange={onChangeInput}
              />
              <span />
              <input
                type={"text"}
                name="max"
                value={inputValues.max}
                onBlur={onBlurInput}
                onFocus={onFocusInput}
                onChange={onChangeInput}
              />
            </div>

            {!isMobile && <Button>{t("Anwenden")}</Button>}
          </form>
        </div>
      )}
    </div>
  );
};

export default ProductFilterRange;
